import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import MBIMetricLegend from "../../MBI/MBIMonthlyBonuses/MBIMetricLegend/MBIMetricLegend";
import defaultContent from "../../../content/earningsCalculator";
import LeafSelector, { OptionType } from "../../../common/leaf/LeafSelector/LeafSelector";
import { LeafCollapsibleSection } from "../../../common/leaf/LeafCollapsibleSection/LeafCollapsibleSection";
import { IconSubsection } from "../../../common/icons/IconSubsection/IconSubsection";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { AWARD_CODES } from "../../../common/enums/award-codes";
import { ConsistencyPaymentSchedule } from "../../../services/EarningsCalculator/earningCalculatorAPI.types";
import {
  setBBIFixedPaymentCurrentAt,
  setBFIFixedPaymentCurrentAt,
  setSelectedBBIOptionId,
  setSelectedBFIOptionId,
} from "../../../reducers/earningCalculator";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  showBBICalculationTrack,
  showBFICalculationTrack,
  showCSICalculationTrack,
  showDifferentialBonusCalculationTrack,
  showPersonalBonusCalculationTrack,
} from "../../../common/tealium/EarningCalculator/EarningCalculatorTrack";
import LeafTileDivider from "../../../common/leaf/LeafTileDivider/LeafTileDivider";
import EarningsCalculatorCalculationTile from "./EarningsCalculatorCalculationTile/EarningsCalculatorCalculationTile";
import EarningsCalculatorDonutChart from "./EarningsCalculatorDonutChart/EarningsCalculatorDonutChart";
import "./EarningsCalculatorIncomeBreakdown.scss";

export const enum BONUS_KPI {
  DIFF_BONUS = "DIFFERENTIAL_BONUS",
  PERSONAL_BONUS = "PERSONAL_BONUS",
  CSI = "CSI",
  BFI = "BFI",
  BBI = "BBI",
  DEFAULT = "DEFAULT",
}

type Props = {
  isLoading: boolean;
  ActiveStatus: {
    IsCSIActive: boolean;
    IsBFIActive: boolean;
    IsBBIActive: boolean;
  };
};

const EarningsCalculatorIncomeBreakdown = ({ isLoading, ActiveStatus }: Props) => {
  const { t } = useTranslation(["earningsCalculator"]);
  const dispatch = useAppDispatch();
  const { ECComputedData } = useAppSelector((state: any) => state.earningCalculator);
  const { IncomeBreakdown, ABOLegs, Settings } = ECComputedData;
  const { BronzeFoundation: bfi, BronzeBuilder: bbi } = IncomeBreakdown;
  const { CurrentAt: bfiOption } = bfi.ConsistencyPayment;
  const { CurrentAt: bbiOption } = bbi.ConsistencyPayment;
  const { CurrentAt: bfiFixedPaymentCurrentRank } = bfi.FixedAmountPayment;
  const { CurrentAt: bbiFixedPaymentCurrentRank } = bbi.FixedAmountPayment;

  const { Active: bfiConsistencyPaymentActive } = Settings.BronzeFoundation.ConsistencyPayment;
  const { Active: bbiConsistencyPaymentActive } = Settings.BronzeBuilder.ConsistencyPayment;

  const { Active: bfiFixedAmountPaymentActive } = Settings.BronzeFoundation.FixedAmountPayment;
  const { Active: bbiFixedAmountPaymentActive } = Settings.BronzeBuilder.FixedAmountPayment;

  const {
    Percentage = [],
    PerfBonusTotal,
    CSI,
    BronzeFoundation,
    BronzeBuilder,
    IncomeBreakdownTotal,
  } = IncomeBreakdown;
  const { IsCSIActive, IsBFIActive, IsBBIActive } = ActiveStatus;

  const getOptions = (schedules: ConsistencyPaymentSchedule[]) =>
    schedules.map(({ Target, Range, Bonus }) => ({
      id: `${Target}`,
      label: t("incomeBreakdownTile.bronze.monthsList", defaultContent["incomeBreakdownTile"]["bronze"]["monthsList"], {
        noOfMonths: Range || Target,
      }),
      value: Bonus.Formatted,
    }));

  const initializeOptionList = (optionsList: OptionType[], selectedValue: number) => {
    return optionsList.map((option) => ({ ...option, selected: Number(option.id) === selectedValue }));
  };

  // Consistency Payment Raw Options
  const BFISelectorOptions = getOptions(Settings.BronzeFoundation.ConsistencyPayment.Schedule);
  const BBISelectorOptions = getOptions(Settings.BronzeBuilder.ConsistencyPayment.Schedule);
  // Fixed Payment Raw Options
  const BFIFixedPaymentOptions = getOptions(Settings.BronzeFoundation.FixedAmountPayment.Schedule);
  const BBIFixedPaymentOptions = getOptions(Settings.BronzeFoundation.FixedAmountPayment.Schedule);
  // Consistency Payment List State
  const [BFIOptionList, setBFIOptionList] = useState(initializeOptionList(BFISelectorOptions, bfiOption));
  const [BBIOptionList, setBBIOptionList] = useState(initializeOptionList(BBISelectorOptions, bbiOption));
  // Fixed Payment List State
  const [BFIFixedPaymentList, setBFIFixedPaymentList] = useState<OptionType[]>([]);
  const [BBIFixedPaymentList, setBBIFixedPaymentList] = useState<OptionType[]>([]);

  // Update Fixed Payment List Selected option
  useEffect(() => {
    setBBIFixedPaymentList(initializeOptionList(BBIFixedPaymentOptions, bbiFixedPaymentCurrentRank));
    setBFIFixedPaymentList(initializeOptionList(BFIFixedPaymentOptions, bfiFixedPaymentCurrentRank));
  }, [bbiFixedPaymentCurrentRank, bfiFixedPaymentCurrentRank]);

  // Consistency Payment Selected Options
  const selectedBFIOption = BFIOptionList.find((option) => option.id === bfiOption.toString());
  const selectedBBIOption = BBIOptionList.find((option) => option.id === bbiOption.toString());

  const [expandedContent, setExpandedContent] = useState(BONUS_KPI.DEFAULT);

  const selectBFIOption = (selectedOption: OptionType) => {
    dispatch(setSelectedBFIOptionId(Number(selectedOption.id)));
    const updatedOptions = BFISelectorOptions.map((option) => ({
      ...option,
      selected: option.id === selectedOption.id,
    }));
    setBFIOptionList(updatedOptions);
  };

  const selectBBIOption = (selectedOption: OptionType) => {
    dispatch(setSelectedBBIOptionId(Number(selectedOption.id)));
    const updatedOptions = BBISelectorOptions.map((option) => ({
      ...option,
      selected: option.id === selectedOption.id,
    }));
    setBBIOptionList(updatedOptions);
  };

  const selecteBFIFixedPaymentRank = (selectedOption: OptionType) => {
    dispatch(setBFIFixedPaymentCurrentAt(Number(selectedOption.id)));

    const updatedList = BFIFixedPaymentOptions.map((option) => ({
      ...option,
      selected: option.id === selectedOption.id,
    }));
    setBFIFixedPaymentList(updatedList);
  };

  const selecteBBIFixedPaymentRank = (selectedOption: OptionType) => {
    dispatch(setBBIFixedPaymentCurrentAt(Number(selectedOption.id)));

    const updatedList = BBIFixedPaymentOptions.map((option) => ({
      ...option,
      selected: option.id === selectedOption.id,
    }));
    setBBIFixedPaymentList(updatedList);
  };

  const sectionOnClick = (_: boolean, id: BONUS_KPI) => {
    const tealiumTrackFunc = {
      PERSONAL_BONUS: showPersonalBonusCalculationTrack,
      DIFFERENTIAL_BONUS: showDifferentialBonusCalculationTrack,
      CSI: showCSICalculationTrack,
      BFI: showBFICalculationTrack,
      BBI: showBBICalculationTrack,
    };

    if (id !== BONUS_KPI.DEFAULT) {
      tealiumTrackFunc[id]();
    }

    setExpandedContent(expandedContent === id ? BONUS_KPI.DEFAULT : id);
  };

  const differentialBonusData = ABOLegs.map((aboLeg: any) => aboLeg.DifferentialBonus);
  return (
    <div className="earnings-calculator-income-breakdown">
      <div className="earnings-calculator-income-breakdown__container">
        <div className="earnings-calculator-income-breakdown__title">
          {`${t("incomeBreakdownTile.title", defaultContent["incomeBreakdownTile"]["title"])}`}
        </div>
        <div className="earnings-calculator-income-breakdown__header">
          <div>{`${t("incomeBreakdownTile.monthlyBonus", defaultContent["incomeBreakdownTile"]["monthlyBonus"])}`}</div>
          <div>{`${t("dashboardTile.simulatedEarnings", defaultContent["dashboardTile"]["simulatedEarnings"])}`}</div>
        </div>
        <div className="earnings-calculator-income-breakdown__content">
          {/* Performance Bonus */}
          <div className="earnings-calculator-income-breakdown__kpi">
            <div className="earnings-calculator-income-breakdown__kpi-header">
              <div className="earnings-calculator-income-breakdown__kpi-header__name">
                <MBIMetricLegend
                  bonusAwd={AWARD_CODES.PERFORMANCE_BONUS_DIFFERENTIAL}
                  theme={"color-dot-title"}
                  title={`${t(
                    "incomeBreakdownTile.performanceBonusTotal.title",
                    defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["title"],
                  )}`}
                />
              </div>
              <div className="earnings-calculator-income-breakdown__kpi-header__value">
                {
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {PerfBonusTotal.BonusAmount.Formatted}
                  </LeafRectangleLoader>
                }
              </div>
            </div>
            {/* Personal */}
            <div className="earnings-calculator-income-breakdown__kpi__expandable-section">
              <LeafCollapsibleSection
                theme="modern"
                titleExpand={`${t(
                  "incomeBreakdownTile.performanceBonusTotal.personal",
                  defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["personal"],
                )}`}
                titleCollapse={`${t(
                  "incomeBreakdownTile.performanceBonusTotal.personal",
                  defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["personal"],
                )}`}
                headerValue={
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {PerfBonusTotal.Personal.Bonus.Formatted}
                  </LeafRectangleLoader>
                }
                prefix={<IconSubsection />}
                onClickFn={sectionOnClick}
                isExpanded={expandedContent === BONUS_KPI.PERSONAL_BONUS}
                useLocalState={false}
                id={BONUS_KPI.PERSONAL_BONUS}
              >
                <EarningsCalculatorCalculationTile
                  IsLoading={isLoading}
                  IncomeData={[PerfBonusTotal.Personal]}
                  Content={{
                    Basis: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.personalMultiplier.basis",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["personalMultiplier"]["basis"],
                    )}`,
                    Multiplier: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.personalMultiplier.multiplier",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["personalMultiplier"][
                        "multiplier"
                      ],
                    )}`,
                    Bonus: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.personalMultiplier.bonus",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["personalMultiplier"]["bonus"],
                    )}`,
                    NotQualified: `${t("notQualified", defaultContent["notQualified"])}`,
                  }}
                />
              </LeafCollapsibleSection>
            </div>
            {/* Differential */}
            <div
              className={`earnings-calculator-income-breakdown__kpi__expandable-section${
                differentialBonusData.length > 0 ? "" : "--hidden"
              }`}
            >
              <LeafCollapsibleSection
                theme="modern"
                titleExpand={`${t(
                  "incomeBreakdownTile.performanceBonusTotal.differential",
                  defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["differential"],
                )}`}
                titleCollapse={`${t(
                  "incomeBreakdownTile.performanceBonusTotal.differential",
                  defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["differential"],
                )}`}
                headerValue={
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {PerfBonusTotal.Differential.BonusAmount.Formatted}
                  </LeafRectangleLoader>
                }
                prefix={<IconSubsection />}
                onClickFn={sectionOnClick}
                isExpanded={expandedContent === BONUS_KPI.DIFF_BONUS}
                useLocalState={false}
                id={BONUS_KPI.DIFF_BONUS}
              >
                <EarningsCalculatorCalculationTile
                  IsLoading={isLoading}
                  IncomeData={differentialBonusData}
                  Content={{
                    Basis: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.differentialMultiplier.basis",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["differentialMultiplier"]["basis"],
                    )}`,
                    BasisWithCount: true,
                    Multiplier: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.differentialMultiplier.multiplier",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["differentialMultiplier"][
                        "multiplier"
                      ],
                    )}`,
                    Bonus: `${t(
                      "incomeBreakdownTile.performanceBonusTotal.differentialMultiplier.bonus",
                      defaultContent["incomeBreakdownTile"]["performanceBonusTotal"]["differentialMultiplier"]["bonus"],
                    )}`,
                    NotQualified: `${t("notQualified", defaultContent["notQualified"])}`,
                  }}
                />
              </LeafCollapsibleSection>
            </div>
          </div>
          {/* CSI */}
          {IsCSIActive && (
            <div className="earnings-calculator-income-breakdown__kpi">
              <LeafTileDivider />
              <LeafCollapsibleSection
                theme="custom"
                prefix={<MBIMetricLegend bonusAwd={AWARD_CODES.CSI} theme={"color-dot-title"} />}
                headerValue={
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {CSI.Bonus.Formatted}
                  </LeafRectangleLoader>
                }
                titleExpand=""
                titleCollapse=""
                onClickFn={sectionOnClick}
                isExpanded={expandedContent === BONUS_KPI.CSI}
                useLocalState={false}
                id={BONUS_KPI.CSI}
              >
                <EarningsCalculatorCalculationTile
                  IsLoading={isLoading}
                  IsQualified={CSI.IsQualified}
                  IncomeData={[CSI]}
                  Content={{
                    Basis: `${t(
                      "incomeBreakdownTile.csi.basis",
                      defaultContent["incomeBreakdownTile"]["csi"]["basis"],
                    )}`,
                    Multiplier: `${t(
                      "incomeBreakdownTile.csi.multiplier",
                      defaultContent["incomeBreakdownTile"]["csi"]["multiplier"],
                    )}`,
                    Bonus: `${t(
                      "incomeBreakdownTile.csi.title",
                      defaultContent["incomeBreakdownTile"]["csi"]["title"],
                    )}`,
                    NotQualified: `${t("notQualified", defaultContent["notQualified"])}`,
                  }}
                />
              </LeafCollapsibleSection>
            </div>
          )}
          {/* BFI */}
          {IsBFIActive && !bfiFixedAmountPaymentActive && (
            <div className="earnings-calculator-income-breakdown__kpi">
              <LeafTileDivider />
              <LeafCollapsibleSection
                theme="custom"
                prefix={<MBIMetricLegend bonusAwd={AWARD_CODES.BRONZE_FOUNDATION} theme={"color-dot-title"} />}
                headerValue={
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {BronzeFoundation.TotalPayment.Formatted}
                  </LeafRectangleLoader>
                }
                mainContent={
                  bfiConsistencyPaymentActive && (
                    <div className="earnings-calculator-income-breakdown__bfi">
                      <div className="earnings-calculator-income-breakdown__bfi__selector">
                        <div className="earnings-calculator-income-breakdown__bfi__selector__label">
                          <LeafSelector options={BFIOptionList} onSelected={selectBFIOption} theme="modern" />
                        </div>
                        <div className="earnings-calculator-income-breakdown__bfi__selector__text">{`${t(
                          "incomeBreakdownTile.bronze.consistency",
                          defaultContent["incomeBreakdownTile"]["bronze"]["consistency"],
                        )}`}</div>
                      </div>
                      <div className="earnings-calculator-income-breakdown__bfi__selector__text">
                        <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                          {selectedBFIOption?.value}
                        </LeafRectangleLoader>
                      </div>
                    </div>
                  )
                }
                titleExpand=""
                titleCollapse=""
                onClickFn={sectionOnClick}
                isExpanded={expandedContent === BONUS_KPI.BFI}
                useLocalState={false}
                id={BONUS_KPI.BFI}
              >
                <EarningsCalculatorCalculationTile
                  IsLoading={isLoading}
                  IsQualified={BronzeFoundation.IsQualified}
                  IncomeData={[BronzeFoundation]}
                  Content={{
                    Basis: `${t(
                      "incomeBreakdownTile.bronze.performanceBonus",
                      defaultContent["incomeBreakdownTile"]["bronze"]["performanceBonus"],
                    )}`,
                    Multiplier: `${t(
                      "incomeBreakdownTile.bronze.bfi.incentiveMultiplier",
                      defaultContent["incomeBreakdownTile"]["bronze"]["bfi"]["incentiveMultiplier"],
                    )}`,
                    Bonus: `${t(
                      "incomeBreakdownTile.bronze.bfi.incentive",
                      defaultContent["incomeBreakdownTile"]["bronze"]["bfi"]["incentive"],
                    )}`,
                    NotQualified: `${t("notQualified", defaultContent["notQualified"])}`,
                  }}
                />
              </LeafCollapsibleSection>
            </div>
          )}

          {/* BFI Fixed Payment */}
          {bfiFixedAmountPaymentActive && (
            <div className="earnings-calculator-income-breakdown__kpi">
              <LeafTileDivider />
              <div className="earnings-calculator-income-breakdown__kpi-header">
                <div className="earnings-calculator-income-breakdown__kpi-header__content">
                  <div className="earnings-calculator-income-breakdown__kpi-header__content__name">
                    <MBIMetricLegend bonusAwd={AWARD_CODES.BRONZE_FOUNDATION} theme={"color-dot-title"} />
                  </div>
                  {bfi.IsQualified && !isLoading && (
                    <div className="earnings-calculator-income-breakdown__kpi-header__content__selector">
                      <LeafSelector
                        options={BFIFixedPaymentList}
                        onSelected={selecteBFIFixedPaymentRank}
                        theme="modern"
                        overridePrefixIcon={() => <></>}
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
                <div className="earnings-calculator-income-breakdown__kpi-header__loader">
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {BronzeFoundation.TotalPayment.Formatted}
                  </LeafRectangleLoader>
                </div>
              </div>
            </div>
          )}

          {/* BBI */}
          {IsBBIActive && !bbiFixedAmountPaymentActive && (
            <div className="earnings-calculator-income-breakdown__kpi">
              <LeafTileDivider />
              <LeafCollapsibleSection
                theme="custom"
                prefix={<MBIMetricLegend bonusAwd={AWARD_CODES.BRONZE_BUILDER} theme={"color-dot-title"} />}
                headerValue={
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {BronzeBuilder.TotalPayment.Formatted}
                  </LeafRectangleLoader>
                }
                mainContent={
                  bbiConsistencyPaymentActive && (
                    <div className="earnings-calculator-income-breakdown__bbi">
                      <div className="earnings-calculator-income-breakdown__bbi__selector">
                        <div className="earnings-calculator-income-breakdown__bbi__selector__label">
                          <LeafSelector options={BBIOptionList} onSelected={selectBBIOption} theme="modern" />
                        </div>
                        <div className="earnings-calculator-income-breakdown__bbi__selector__text">{`${t(
                          "incomeBreakdownTile.bronze.consistency",
                          defaultContent["incomeBreakdownTile"]["bronze"]["consistency"],
                        )}`}</div>
                      </div>
                      <div className="earnings-calculator-income-breakdown__bbi__selector__text">
                        <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                          {selectedBBIOption?.value}
                        </LeafRectangleLoader>
                      </div>
                    </div>
                  )
                }
                titleExpand=""
                titleCollapse=""
                onClickFn={sectionOnClick}
                isExpanded={expandedContent === BONUS_KPI.BBI}
                useLocalState={false}
                id={BONUS_KPI.BBI}
              >
                <EarningsCalculatorCalculationTile
                  IsLoading={isLoading}
                  IsQualified={BronzeBuilder.IsQualified}
                  IncomeData={[BronzeBuilder]}
                  Content={{
                    Basis: `${t(
                      "incomeBreakdownTile.bronze.performanceBonus",
                      defaultContent["incomeBreakdownTile"]["bronze"]["performanceBonus"],
                    )}`,
                    Multiplier: `${t(
                      "incomeBreakdownTile.bronze.bbi.incentiveMultiplier",
                      defaultContent["incomeBreakdownTile"]["bronze"]["bbi"]["incentiveMultiplier"],
                    )}`,
                    Bonus: `${t(
                      "incomeBreakdownTile.bronze.bbi.incentive",
                      defaultContent["incomeBreakdownTile"]["bronze"]["bbi"]["incentive"],
                    )}`,
                    NotQualified: `${t("notQualified", defaultContent["notQualified"])}`,
                  }}
                />
              </LeafCollapsibleSection>
            </div>
          )}

          {/* BBI Fixed Payment */}
          {bbiFixedAmountPaymentActive && (
            <div className="earnings-calculator-income-breakdown__kpi">
              <LeafTileDivider />
              <div className="earnings-calculator-income-breakdown__kpi-header">
                <div className="earnings-calculator-income-breakdown__kpi-header__content">
                  <div className="earnings-calculator-income-breakdown__kpi-header__content__name">
                    <MBIMetricLegend bonusAwd={AWARD_CODES.BRONZE_BUILDER} theme={"color-dot-title"} />
                  </div>
                  {bbi.IsQualified && !isLoading && (
                    <div className="earnings-calculator-income-breakdown__kpi-header__content__selector">
                      <LeafSelector
                        options={BBIFixedPaymentList}
                        onSelected={selecteBBIFixedPaymentRank}
                        theme="modern"
                        overridePrefixIcon={() => <></>}
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
                <div className="earnings-calculator-income-breakdown__kpi-header__loader">
                  <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"100%"}>
                    {BronzeBuilder.TotalPayment.Formatted}
                  </LeafRectangleLoader>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="earnings-calculator-income-breakdown__subtotal">
          <span className="earnings-calculator-income-breakdown__subtotal_text">{`${t(
            "incomeBreakdownTile.monthlyBonusSubtotal",
            defaultContent["incomeBreakdownTile"]["monthlyBonusSubtotal"],
          )}`}</span>
          <span className="earnings-calculator-income-breakdown__subtotal_amount">
            <LeafRectangleLoader isLoading={isLoading} width={"100%"} height={"30px"} margin={"0"}>
              {IncomeBreakdownTotal.Formatted}
            </LeafRectangleLoader>
          </span>
        </div>
      </div>
      <EarningsCalculatorDonutChart isLoading={isLoading} percentage={Percentage} />
    </div>
  );
};
export default EarningsCalculatorIncomeBreakdown;
